import React, { useCallback, useEffect, useState } from 'react';
import { useI18n } from '../../i18n/I18nContext';
import { LoadingSpinner, Toast } from './SettingWidgets';
import { WhatsAppTemplateCard } from './WhatsAppTemplateCard';
import { whatsAppStore, type WhatsAppGroupRecord, type WhatsAppTemplateRecord } from './WhatsAppStore';

export function WhatsAppPage() {
  const { t } = useI18n();
  const [groups, setGroups] = useState<WhatsAppGroupRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [open, setOpen] = useState<Record<string, boolean>>({});

  const showToast = useCallback((type: 'success' | 'error', message: string) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3000);
  }, []);

  useEffect(() => {
    whatsAppStore
      .fetch()
      .then(setGroups)
      .catch(() => showToast('error', t('settings.whatsapp.loadFailed')))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showToast]);

  /** Swap one template in place — the API answers with the resolved record. */
  const replaceTemplate = (updated: WhatsAppTemplateRecord) => {
    setGroups((prev) =>
      prev.map((group) => ({
        ...group,
        templates: group.templates.map((template) =>
          template.code === updated.code ? updated : template,
        ),
      })),
    );
  };

  const handleSave = async (code: string, body: string, enabled: boolean) => {
    try {
      replaceTemplate(await whatsAppStore.save(code, body, enabled));
      showToast('success', t('settings.whatsapp.saveSuccess'));
    } catch (error) {
      showToast(
        'error',
        error instanceof Error ? error.message : t('settings.whatsapp.saveFailed'),
      );
    }
  };

  const handleReset = async (code: string) => {
    try {
      replaceTemplate(await whatsAppStore.reset(code));
      showToast('success', t('settings.whatsapp.resetSuccess'));
    } catch {
      showToast('error', t('settings.whatsapp.resetFailed'));
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="mx-auto max-w-4xl space-y-5">
      <Toast toast={toast} />

      <div className="rounded-xl border border-slate-200 bg-white px-4 py-5 shadow-sm sm:px-6 dark:border-slate-700 dark:bg-slate-900">
        <h1 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
          {t('settings.whatsapp.title')}
        </h1>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
          {t('settings.whatsapp.subtitle')}
        </p>
        <p className="mt-3 rounded-lg bg-slate-50 px-3 py-2.5 text-xs leading-relaxed text-slate-600 dark:bg-slate-800 dark:text-slate-400">
          {t('settings.whatsapp.manualHint')}
        </p>
      </div>

      {groups.map((group) => {
        const expanded = open[group.group] ?? false;
        const enabledCount = group.templates.filter((template) => template.enabled).length;

        return (
          <div
            key={group.group}
            className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-900"
          >
            <button
              type="button"
              aria-expanded={expanded}
              onClick={() => setOpen((prev) => ({ ...prev, [group.group]: !expanded }))}
              className="flex w-full items-center gap-3 px-4 py-4 text-left transition hover:bg-slate-50 sm:px-6 dark:hover:bg-slate-800"
            >
              <svg
                className={`h-4 w-4 shrink-0 text-slate-400 transition-transform ${expanded ? 'rotate-90' : ''}`}
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={2}
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
              </svg>
              <span className="flex-1 text-base font-semibold text-slate-900 dark:text-slate-100">
                {t(`settings.whatsapp.groups.${group.group}`)}
              </span>
              <span className="text-xs tabular-nums text-slate-500 dark:text-slate-400">
                {t('settings.whatsapp.groupCount', {
                  enabled: enabledCount,
                  total: group.templates.length,
                })}
              </span>
            </button>

            {expanded && (
              <div className="space-y-4 border-t border-slate-100 bg-slate-50/60 px-4 py-5 sm:px-6 dark:border-slate-800 dark:bg-slate-950/30">
                {group.templates.map((template) => (
                  <WhatsAppTemplateCard
                    key={template.code}
                    template={template}
                    onSave={handleSave}
                    onReset={handleReset}
                  />
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
